import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  AreaChart,
  ComposedChart,
  ReferenceLine,
  Customized,
  Line as RechartsLine,
} from 'recharts';

export interface ActivityStreamPoint {
  time?: number;
  distance?: number;
  altitude?: number;
  heartRate?: number;
  cadence?: number;
  velocity?: number;
  latitude?: number;
  longitude?: number;
}

export interface ActivityInterval {
  name?: string;
  intensity?: string;
  startTime?: number;
  elapsedTime: number;
  distance?: number;
  averageSpeed?: number;
  targetPace?: number;
}

interface StreamChartsProps {
  streams: ActivityStreamPoint[] | null;
  intervals?: ActivityInterval[] | null;
}

const mpsToPace = (mps: number): number | null => {
  if (!mps || mps <= 0) return null;
  const paceSecPerMi = 1609.34 / mps;
  return paceSecPerMi;
};

const formatPace = (secPerMi: number): string => {
  const m = Math.floor(secPerMi / 60);
  const s = Math.round(secPerMi % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const metersToFeet = (m: number): number => m * 3.28084;

const intervalBarPaddingPx = 2;
const intervalBarRadiusPx = 6;
const paceAxisIntervalSeconds = 15;

const renderIntervalBars = (props: any) => {
  const xAxis = props.xAxisMap?.[0];
  const yAxis = props.yAxisMap?.[0];
  const data = Array.isArray(props.data) ? props.data : [];

  if (!xAxis?.scale || !yAxis?.scale || data.length === 0) return null;

  const xScale = xAxis.scale;
  const yScale = yAxis.scale;
  const baseline = yScale(props.baselineValue || 0);

  return (
    <g>
      {data.map((interval: any) => {
        const startX = xScale(interval.startDistance);
        const endX = xScale(interval.endDistance);
        const paddedX = Math.min(startX, endX) + intervalBarPaddingPx;
        const width = Math.max(
          1,
          Math.abs(endX - startX) - intervalBarPaddingPx * 2,
        );
        const valueY = yScale(interval.speed);
        const y = Math.min(valueY, baseline);
        const height = Math.abs(baseline - valueY);

        return (
          <rect
            key={interval.name}
            x={paddedX}
            y={y}
            width={width}
            height={height}
            rx={intervalBarRadiusPx}
            ry={intervalBarRadiusPx}
            fill={interval.fill}
          />
        );
      })}
    </g>
  );
};

const StreamCharts = ({ streams, intervals }: StreamChartsProps) => {
  const streamList = Array.isArray(streams) ? streams : [];
  const intervalList = Array.isArray(intervals) ? intervals : [];

  if (streamList.length === 0 && intervalList.length === 0) return null;

  const hasHeartRate = streamList.some((s) => s.heartRate != null);
  const hasCadence = streamList.some((s) => s.cadence != null);
  const hasAltitude = streamList.some((s) => s.altitude != null);
  const hasPace = streamList.some((s) => s.velocity != null && s.velocity > 0);
  const hasDistance = streamList.some((s) => s.distance != null);
  const shouldShowPaceChart = hasPace && hasDistance;
  const hasIntervals = intervalList.length > 0;

  // Build chart data — use distance (miles) as X axis when available, else time
  const useDistanceAxis = hasDistance || hasIntervals;
  const chartData = streamList.map((s) => {
    const x = useDistanceAxis ? (s.distance || 0) / 1609.34 : s.time || 0;
    return {
      x: Math.round(x * 100) / 100,
      heartRate: s.heartRate,
      cadence: s.cadence,
      altitude:
        s.altitude != null ? Math.round(metersToFeet(s.altitude)) : null,
      pace: s.velocity != null && s.velocity > 0 ? mpsToPace(s.velocity) : null,
    };
  });

  const intervalChartData = hasIntervals
    ? intervalList.map((interval, index) => {
        const distanceMiles = interval.distance
          ? interval.distance / 1609.34
          : undefined;
        const paceFromDistance = distanceMiles
          ? interval.elapsedTime / distanceMiles
          : undefined;
        const paceFromSpeed = interval.averageSpeed
          ? mpsToPace(interval.averageSpeed)
          : undefined;
        const pace =
          interval.targetPace || paceFromDistance || paceFromSpeed || 0;
        const startDistance =
          intervalList
            .slice(0, index)
            .reduce((total, item) => total + (item.distance || 0), 0) / 1609.34;
        const endDistance = startDistance + (distanceMiles || 0);
        return {
          name: interval.name || `Lap ${index + 1}`,
          cumulativeDistance: Math.round(endDistance * 100) / 100,
          startDistance,
          endDistance,
          midpointDistance: (startDistance + endDistance) / 2,
          distance: distanceMiles ? Math.round(distanceMiles * 100) / 100 : 0,
          elapsedTime: interval.elapsedTime,
          pace,
          speed: pace > 0 ? 3600 / pace : null,
          intensity: String(interval.intensity || '').toLowerCase(),
          index,
        };
      })
    : [];

  const targetedIntervalSpeeds = intervalChartData
    .map((interval) => interval.speed)
    .filter((speed): speed is number => speed != null && speed > 0);
  const maximumIntervalPace = Math.max(
    0,
    ...intervalChartData.map((interval) => interval.pace),
  );
  const normalizedMaximumIntervalPace =
    Math.ceil((maximumIntervalPace * 1.1) / paceAxisIntervalSeconds) *
    paceAxisIntervalSeconds;
  const intervalYAxisMin =
    normalizedMaximumIntervalPace > 0
      ? 3600 / normalizedMaximumIntervalPace
      : 0;
  const minimumTargetedIntervalSpeed = Math.min(...targetedIntervalSpeeds);
  const untargetedIntervalSpeed =
    targetedIntervalSpeeds.length > 0
      ? intervalYAxisMin +
        (minimumTargetedIntervalSpeed - intervalYAxisMin) * 0.5
      : 1;
  const displayIntervalChartData = intervalChartData.map((interval) => {
    const isWarmup = interval.intensity.includes('warmup');
    const isCooldown = interval.intensity.includes('cooldown');
    return {
      ...interval,
      hasTargetPace: interval.speed != null,
      speed: interval.speed || untargetedIntervalSpeed,
      fill: isWarmup
        ? 'rgba(251, 191, 36, 0.9)'
        : isCooldown
          ? 'rgba(56, 189, 248, 0.9)'
          : interval.index % 2 === 0
            ? 'rgba(59, 130, 246, 0.85)'
            : 'rgba(37, 99, 235, 0.95)',
    };
  });

  const averageIntervalSpeed =
    targetedIntervalSpeeds.length > 0
      ? targetedIntervalSpeeds.reduce((total, speed) => total + speed, 0) /
        targetedIntervalSpeeds.length
      : undefined;

  const maximumIntervalSpeed = Math.max(
    0,
    ...displayIntervalChartData.map((interval) => interval.speed),
  );
  const intervalYAxisMax =
    maximumIntervalSpeed > 0 ? maximumIntervalSpeed * 1.05 : 1;

  const streamDistanceMax = useDistanceAxis
    ? Math.max(...streamList.map((s) => (s.distance || 0) / 1609.34))
    : 0;
  const intervalDistanceMax =
    intervalChartData.length > 0
      ? Math.max(...intervalChartData.map((interval) => interval.endDistance))
      : 0;
  const intervalXAxisMax = Math.max(streamDistanceMax, intervalDistanceMax);

  const xLabel = useDistanceAxis ? 'mi' : 'time';
  const xFormatter = useDistanceAxis
    ? (v: number) => `${v}mi`
    : (v: number) => formatTime(v);

  // Leave enough room for the first Y-axis label instead of clipping it
  // against the card edge on narrow screens.
  const chartMargin = { top: 2, right: 4, left: 4, bottom: 0 };

  const chartUnitClassName =
    'mb-1 text-[10px] font-bold uppercase leading-none tracking-[0.14em] text-brand-cobalt';

  const tooltipStyle = {
    backgroundColor: '#0d1529',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '12px',
  };

  return (
    <div className="space-y-4">
      {/* Intervals */}
      {hasIntervals ? (
        <div className="telemetry-panel">
          <h3 className="telemetry-heading">Intervals</h3>
          <div className={chartUnitClassName}>min/mi</div>
          <ResponsiveContainer width="100%" height={210}>
            <ComposedChart data={displayIntervalChartData} margin={chartMargin}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgb(148 163 184 / 0.18)"
              />
              <XAxis
                dataKey="midpointDistance"
                type="number"
                domain={[0, intervalXAxisMax]}
                tickFormatter={(v: number) => `${Math.round(v * 100) / 100}mi`}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                interval="preserveStartEnd"
                minTickGap={35}
              />
              <YAxis
                domain={[intervalYAxisMin, intervalYAxisMax]}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={(v: number) =>
                  v > 0 ? formatPace(3600 / v) : ''
                }
                width={36}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v: any) => `${v} mi`}
                formatter={(v: any, name: any, item: any) => {
                  if (name === 'speed') {
                    return [
                      item.payload.hasTargetPace
                        ? `${formatPace(item.payload.pace)}/mi · ${item.payload.distance} mi`
                        : `No target pace · ${item.payload.distance} mi`,
                      item.payload.name,
                    ];
                  }
                  return [v, name];
                }}
              />
              {averageIntervalSpeed ? (
                <ReferenceLine
                  y={averageIntervalSpeed}
                  stroke="#9ca3af"
                  strokeDasharray="6 6"
                />
              ) : null}
              <Customized
                component={renderIntervalBars}
                data={displayIntervalChartData}
                baselineValue={intervalYAxisMin}
              />
              <RechartsLine
                dataKey="speed"
                stroke="transparent"
                dot={false}
                activeDot={{ r: 4, fill: '#2563eb', stroke: '#ffffff' }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {/* Heart Rate */}
      {hasHeartRate ? (
        <div className="telemetry-panel">
          <h3 className="telemetry-heading">Heart Rate</h3>
          <div className={chartUnitClassName}>bpm</div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={chartData} margin={chartMargin}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgb(148 163 184 / 0.18)"
              />
              <XAxis
                dataKey="x"
                tickFormatter={xFormatter}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={36} />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v: any) => `${v} ${xLabel}`}
                formatter={(v: any) => [`${v} bpm`, 'HR']}
              />
              <Line
                type="monotone"
                dataKey="heartRate"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {/* Pace */}
      {shouldShowPaceChart ? (
        <div className="telemetry-panel">
          <h3 className="telemetry-heading">Pace</h3>
          <div className={chartUnitClassName}>min/mi</div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={chartData} margin={chartMargin}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgb(148 163 184 / 0.18)"
              />
              <XAxis
                dataKey="x"
                tickFormatter={xFormatter}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                tickFormatter={(v: number) => formatPace(v)}
                width={36}
                reversed
              />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v: any) => `${v} ${xLabel}`}
                formatter={(v: any) => [formatPace(v), 'Pace']}
              />
              <Line
                type="monotone"
                dataKey="pace"
                stroke="#c4ff00"
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {/* Cadence */}
      {hasCadence ? (
        <div className="telemetry-panel">
          <h3 className="telemetry-heading">Cadence</h3>
          <div className={chartUnitClassName}>spm</div>
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={chartData} margin={chartMargin}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgb(148 163 184 / 0.18)"
              />
              <XAxis
                dataKey="x"
                tickFormatter={xFormatter}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={36} />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v: any) => `${v} ${xLabel}`}
                formatter={(v: any) => [`${v} spm`, 'Cadence']}
              />
              <Line
                type="monotone"
                dataKey="cadence"
                stroke="#1e88e5"
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      {/* Elevation Profile */}
      {hasAltitude ? (
        <div className="telemetry-panel">
          <h3 className="telemetry-heading">Elevation</h3>
          <div className={chartUnitClassName}>ft</div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={chartData} margin={chartMargin}>
              <defs>
                <linearGradient id="elevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="x"
                tickFormatter={xFormatter}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} width={36} />
              <Tooltip
                contentStyle={tooltipStyle}
                labelFormatter={(v: any) => `${v} ${xLabel}`}
                formatter={(v: any) => [`${v} ft`, 'Elevation']}
              />
              <Area
                type="monotone"
                dataKey="altitude"
                stroke="#f59e0b"
                strokeWidth={2}
                fill="url(#elevGrad)"
                connectNulls
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </div>
  );
};

export default StreamCharts;
